import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Textarea,
  Stack,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/joy'
import { ArrowLeft as ArrowBack, FloppyDisk as Save, CurrencyDollar as AttachMoney, CheckCircle } from '@phosphor-icons/react'
import { invoicesApi, paymentsApi } from '../lib/billingApi'
import type { PaymentFormData, PaymentRecord } from '../types/billing'
import PaymentReceiptActions from '../components/billing/PaymentReceiptActions'

export default function PaymentCreate() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const invoiceId = searchParams.get('invoice')
  
  const [formData, setFormData] = useState<PaymentFormData>({
    invoice_id: Number(invoiceId) || 0,
    amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'ach',
    reference_number: '',
    notes: '',
  })
  
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [recordedPayment, setRecordedPayment] = useState<PaymentRecord | null>(null)
  // The customer's usual method is a default, never an override of a choice
  // the user has already made on this form.
  const methodTouched = useRef(false)

  // Fetch invoice details if invoice ID is provided
  const { data: invoice, isLoading: invoiceLoading } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => invoicesApi.get(Number(invoiceId)),
    enabled: !!invoiceId,
  })

  // Fetch unpaid invoices for the dropdown when no specific invoice is preselected
  const { data: unpaidInvoices } = useQuery({
    queryKey: ['invoices', 'unpaid'],
    queryFn: () => invoicesApi.list({ overdue_only: false, page_size: 200 } as any),
    enabled: !invoiceId,
  })

  // How this customer paid last. Everything else on this form is derived —
  // amount from the balance, date from today — so the method was the one field
  // still being hand-picked, almost always to the same answer as last time.
  // The invoice arrives either in the URL or through the picker below.
  const pickedInvoice = unpaidInvoices?.results?.find(inv => inv.id === formData.invoice_id)
  const customerId = invoice?.customer ?? invoice?.contact_id ?? pickedInvoice?.customer ?? pickedInvoice?.contact_id
  const { data: customerPayments } = useQuery({
    queryKey: ['payments', 'customer', customerId],
    queryFn: () => paymentsApi.list({ customer_id: customerId as number }),
    enabled: !!customerId,
  })

  // Create payment mutation
  const createMutation = useMutation({
    mutationFn: (data: PaymentFormData) => paymentsApi.create(data),
    onSuccess: (payment) => {
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['invoice', String(payment.invoice_id || formData.invoice_id)] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', String(payment.invoice_id || formData.invoice_id)] })
      setRecordedPayment(payment)
    },
    onError: (error: any) => {
      if (error.response?.data) {
        setErrors(error.response.data)
      }
    },
  })
  
  useEffect(() => {
    if (invoice) {
      // Set amount to remaining balance
      const remainingBalance = parseFloat(invoice.balance_due || invoice.total_amount)
      setFormData(prev => ({
        ...prev,
        amount: remainingBalance,
      }))
    }
  }, [invoice])

  // Payments come back newest first, so the first row is how they last paid.
  useEffect(() => {
    if (methodTouched.current) return
    const lastMethod = customerPayments?.results?.[0]?.payment_method
    if (lastMethod) {
      setFormData(prev => ({ ...prev, payment_method: lastMethod }))
    }
  }, [customerPayments])
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    
    // Validate
    const newErrors: Record<string, string> = {}
    if (!formData.invoice_id) {
      newErrors.invoice_id = 'Invoice is required'
    }
    if (formData.amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0'
    }
    if (!formData.payment_date) {
      newErrors.payment_date = 'Payment date is required'
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    
    createMutation.mutate(formData)
  }
  
  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num)
  }
  
  if (recordedPayment) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720, mx: 'auto' }}>
        <Card variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <CardContent>
            <Stack spacing={2.5}>
              <CheckCircle size={40} weight="fill" color="var(--joy-palette-success-500)" />
              <Box>
                <Typography level="h2">Payment recorded</Typography>
                <Typography level="body-lg" sx={{ mt: 1 }}>
                  {formatCurrency(recordedPayment.amount)} for invoice #{recordedPayment.invoice_number || invoice?.document_number}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {recordedPayment.customer_name || invoice?.contact_name}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography level="title-lg">Customer receipt</Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5, mb: 2 }}>
                  Download a PDF or review an email with the receipt attached. You can also do this later from the invoice’s Payments tab.
                </Typography>
                <PaymentReceiptActions paymentId={recordedPayment.id} />
              </Box>
              <Button onClick={() => navigate(`/operations/billing/invoices/${recordedPayment.invoice_id || formData.invoice_id}`)}>
                View invoice
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    )
  }

  if (invoiceLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          component={Link}
          to={invoiceId ? `/operations/billing/invoices/${invoiceId}` : '/operations/billing/payments'}
          startDecorator={<ArrowBack />}
          variant="plain"
          size="sm"
          sx={{ mb: 2 }}
        >
          Back to {invoiceId ? 'Invoice' : 'Invoices'}
        </Button>
        
        <Typography level="h1">Record Payment</Typography>
        <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1 }}>
          After saving, you can download a PDF receipt or review an email to send to the customer.
        </Typography>
        {invoice && (
          <Typography level="body-md" sx={{ color: 'text.secondary', mt: 1 }}>
            For Invoice #{invoice.document_number}
          </Typography>
        )}
      </Box>
      
      <form onSubmit={handleSubmit}>
        <Card variant="outlined">
          <CardContent>
            {/* Invoice Summary */}
            {invoice && (
              <>
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                  <Typography level="title-md" sx={{ mb: 2 }}>Invoice Summary</Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography level="body-sm">Customer</Typography>
                      <Typography level="body-sm" fontWeight="lg">
                        {invoice.contact_name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography level="body-sm">Invoice Total</Typography>
                      <Typography level="body-sm" fontWeight="lg">
                        {formatCurrency(invoice.total_amount)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography level="body-sm">Amount Paid</Typography>
                      <Typography level="body-sm" fontWeight="lg" color="success">
                        {formatCurrency(invoice.amount_paid || 0)}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography level="body-sm" fontWeight="lg">Balance Due</Typography>
                      <Typography level="body-sm" fontWeight="lg" color="primary">
                        {formatCurrency(invoice.balance_due || 0)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                <Divider sx={{ mb: 3 }} />
              </>
            )}
            
            {/* Payment Form */}
            <Stack spacing={3}>
              {!invoiceId && (
                <FormControl required error={!!errors.invoice_id}>
                  <FormLabel>Invoice</FormLabel>
                  <Select
                    value={formData.invoice_id}
                    onChange={(_, value) => {
                      const selectedId = value as number
                      const selected = unpaidInvoices?.results?.find((inv) => inv.id === selectedId)
                      setFormData({
                        ...formData,
                        invoice_id: selectedId,
                        amount: selected ? parseFloat(selected.balance_due || selected.total_amount) : 0,
                      })
                    }}
                  >
                    <Option value={0}>Select an invoice</Option>
                    {unpaidInvoices?.results
                      ?.filter((inv) => parseFloat(inv.balance_due || '0') > 0)
                      .map((inv) => (
                        <Option key={inv.id} value={inv.id}>
                          #{inv.document_number} — {inv.contact_name} ({formatCurrency(inv.balance_due || 0)})
                        </Option>
                      ))}
                  </Select>
                  {errors.invoice_id && (
                    <Typography level="body-sm" color="danger">{errors.invoice_id}</Typography>
                  )}
                </FormControl>
              )}
              
              <FormControl required error={!!errors.amount}>
                <FormLabel>Payment Amount</FormLabel>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  startDecorator={<AttachMoney />}
                  slotProps={{
                    input: {
                      step: 0.01,
                      min: 0.01,
                      max: invoice ? parseFloat(invoice.balance_due || invoice.total_amount) : undefined,
                    },
                  }}
                />
                {errors.amount && (
                  <Typography level="body-sm" color="danger">{errors.amount}</Typography>
                )}
                {invoice && formData.amount > 0 && (
                  <Typography level="body-sm" sx={{ mt: 1 }}>
                    {formData.amount < parseFloat(invoice.balance_due || '0')
                      ? `Partial payment - Remaining balance will be ${formatCurrency(
                          parseFloat(invoice.balance_due || '0') - formData.amount
                        )}`
                      : formData.amount === parseFloat(invoice.balance_due || '0')
                      ? 'This will fully pay the invoice'
                      : 'Warning: Payment exceeds balance due'}
                  </Typography>
                )}
              </FormControl>
              
              <FormControl required error={!!errors.payment_date}>
                <FormLabel>Payment Date</FormLabel>
                <Input
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                />
                {errors.payment_date && (
                  <Typography level="body-sm" color="danger">{errors.payment_date}</Typography>
                )}
              </FormControl>
              
              <FormControl required>
                <FormLabel>Payment Method</FormLabel>
                <Select
                  value={formData.payment_method}
                  onChange={(_, value) => {
                    methodTouched.current = true
                    setFormData({ ...formData, payment_method: value as any })
                  }}
                >
                  <Option value="credit_card">Credit Card</Option>
                  <Option value="ach">ACH/Bank Transfer</Option>
                  <Option value="check">Check</Option>
                  <Option value="cash">Cash</Option>
                  <Option value="wire_transfer">Wire Transfer</Option>
                  <Option value="other">Other</Option>
                </Select>
              </FormControl>
              
              <FormControl>
                <FormLabel>Reference Number</FormLabel>
                <Input
                  value={formData.reference_number}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  placeholder="Check number, transaction ID, etc."
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes about this payment"
                  minRows={3}
                />
              </FormControl>
            </Stack>
          </CardContent>
        </Card>
        
        {/* Actions */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            component={Link}
            to={invoiceId ? `/operations/billing/invoices/${invoiceId}` : '/operations/billing/payments'}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="solid"
            color="primary"
            startDecorator={<Save />}
            loading={createMutation.isPending}
          >
            Record Payment
          </Button>
        </Box>
      </form>
      
      {createMutation.isError && (
        <Alert color="danger" sx={{ mt: 2 }}>
          Error recording payment. Please try again.
        </Alert>
      )}
    </Box>
  )
}
